import React, { useEffect, useState } from "react";
import { Button, Stack, styled, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  createTeam,
  getAllTeams,
  removeTeam,
  uploadTeamAvatar,
} from "../../api/team.api.js";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CreateTeamModal from "../../components/modals/createTeam.modal.js";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "../../context/snackbar.context.js";

const AdminTeamsPage = () => {
  const showSnackbar = useSnackbar();
  const [refresh, setRefresh] = useState(false);
  const [teams, setTeams] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    getAllTeams().then((response) => {
      setTeams(response.data);
    });
  }, [refresh]);

  const handleUpload = (id, file) => {
    uploadTeamAvatar(id, file)
      .then(() => showSnackbar("Image uploaded!", "success"))
      .catch(() => showSnackbar("Upload failed!", "error"));
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSubmit = (formData) => {
    createTeam(formData)
      .then(() => {
        setRefresh((prev) => !prev);
        showSnackbar("Team created!", "success");
      })
      .catch((e) => showSnackbar("Failed to create team!", "error"));
  };

  const handleRemove = (id) => {
    removeTeam(id)
      .then(() => {
        setRefresh((prev) => !prev);
        showSnackbar("Team removed!", "success");
      })
      .catch((e) => showSnackbar("Failed to remove team!", "error"));
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 200,
    },
    {
      field: "members",
      headerName: "Member Count",
      filterable: false,
      width: 150,
      renderCell: (params) => <div>{params.row.members.length}</div>,
    },
    {
      field: "isGenerated",
      headerName: "Generated",
      width: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 400,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <Stack
          display="flex"
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="center"
        >
          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<FileUploadIcon />}
          >
            Upload Avatar
            <VisuallyHiddenInput
              type="file"
              onChange={(event) =>
                handleUpload(params.row.id, event.target.files[0])
              }
            />
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemove(params.row.id)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  return (
    <div>
      <Typography variant="h3" gutterBottom>
        Team Administration
      </Typography>
      <DataGrid
        rows={teams}
        columns={columns}
        sx={{ border: 0 }}
        disable
        SelectionOnClick
        disableColumnSelector
        getRowHeight={() => "auto"}
      />

      <Button variant="contained" onClick={handleOpenModal} sx={{ mt: 1 }}>
        Create team
      </Button>

      <CreateTeamModal
        aria-modal
        open={openModal}
        handleClose={handleCloseModal}
        handleSubmit={handleSubmit}
      />
    </div>
  );
};

export default AdminTeamsPage;
